import { useEffect, useState } from "react";
import { api, adminAuth, imageUrl } from "../../lib/api";
import { toast } from "sonner";
import { Upload, RotateCcw, Save } from "lucide-react";

const SLOTS = [
  { key: "hero_bg", label: "Hero Background", hint: "Large photo behind the top banner", ratio: "aspect-[16/9]" },
  { key: "handmade_card", label: "MawMaw's Handmade Card", hint: "Tall card on the right of the hero", ratio: "aspect-[3/4]" },
  { key: "category_tshirt", label: "Custom Tees Tile", hint: "Blank tee category photo", ratio: "aspect-[16/10]" },
  { key: "category_tumbler", label: "Custom Tumblers Tile", hint: "Blank tumbler category photo", ratio: "aspect-[16/10]" },
  { key: "story_photo", label: "Papa G & MawMaw Story Photo", hint: "Photo in the Our Story section", ratio: "aspect-[4/5]" },
];

const TEXT_FIELDS = [
  { key: "hero_heading", label: "Hero Headline", hint: "Main headline at the top of the homepage", rows: 3 },
  { key: "hero_subheading", label: "Hero Sub-headline", hint: "Gold accent line under the headline", rows: 2 },
  { key: "hero_description", label: "Hero Description", hint: "Small line below (e.g. 'Crafted in East Texas.')", rows: 2 },
];

export default function StorefrontTab() {
  const [images, setImages] = useState({});
  const [custom, setCustom] = useState({});
  const [uploadingKey, setUploadingKey] = useState(null);
  const [text, setText] = useState({});
  const [pickup, setPickup] = useState({});
  const [savingText, setSavingText] = useState(false);
  const [savingPickup, setSavingPickup] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/storefront", { headers: adminAuth() });
      setImages(r.data.images || {});
      setCustom(r.data.custom || {});
      setText(r.data.text || {});
      setPickup(r.data.pickup || {});
    } catch { toast.error("Failed to load storefront settings"); }
  };

  useEffect(() => { load(); }, []);

  const saveText = async () => {
    setSavingText(true);
    try {
      const r = await api.patch("/admin/storefront/settings", { text }, { headers: adminAuth() });
      setText(r.data.text || {});
      toast.success("Homepage text saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingText(false); }
  };

  const savePickup = async () => {
    setSavingPickup(true);
    try {
      const r = await api.patch("/admin/storefront/settings", { pickup }, { headers: adminAuth() });
      setPickup(r.data.pickup || {});
      toast.success("Pickup details saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingPickup(false); }
  };

  const upload = async (key, file) => {
    if (!file) return;
    setUploadingKey(key);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/admin/storefront/${key}/image`, fd, {
        headers: { ...adminAuth(), "Content-Type": "multipart/form-data" },
      });
      setImages((prev) => ({ ...prev, [key]: r.data.image_url }));
      setCustom((prev) => ({ ...prev, [key]: true }));
      toast.success("Image updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingKey(null);
    }
  };

  const reset = async (key) => {
    if (!window.confirm("Reset this image back to the original photo?")) return;
    try {
      const r = await api.delete(`/admin/storefront/${key}/image`, { headers: adminAuth() });
      setImages((prev) => ({ ...prev, [key]: r.data.image_url }));
      setCustom((prev) => ({ ...prev, [key]: false }));
      toast.success("Reset to original");
    } catch { toast.error("Reset failed"); }
  };

  return (
    <div className="card-rustic" data-testid="admin-storefront-tab">
      <div className="p-5 border-b border-[rgba(27,42,65,0.1)]">
        <div className="font-mono-ui text-[10px] tracking-[0.3em] text-[var(--crimson)]">/STOREFRONT</div>
        <div className="font-serif-display text-2xl text-[var(--navy)]">Homepage & Pickup</div>
        <div className="text-xs text-[var(--ink-soft)] mt-1">Swap the front-page photos, edit the hero text, and set your pickup address & hours.</div>
      </div>

      {/* HOMEPAGE TEXT */}
      <div className="p-5 border-b border-[rgba(27,42,65,0.1)]" data-testid="storefront-text-section">
        <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)] mb-3">Homepage Hero Text</div>
        <div className="grid gap-4 max-w-2xl">
          {TEXT_FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--navy)] block mb-1">{f.label}</span>
              <span className="text-[11px] text-[var(--ink-soft)] block mb-1.5">{f.hint}</span>
              <textarea
                rows={f.rows}
                value={text[f.key] ?? ""}
                onChange={(e) => setText((prev) => ({ ...prev, [f.key]: e.target.value }))}
                className="input-field resize-y"
                data-testid={`storefront-text-${f.key}`}
              />
            </label>
          ))}
        </div>
        <button type="button" onClick={saveText} disabled={savingText} className="btn-primary mt-4 text-sm" data-testid="storefront-text-save">
          <Save className="w-3.5 h-3.5"/> {savingText ? "Saving…" : "Save Text"}
        </button>
      </div>

      {/* PICKUP DETAILS */}
      <div className="p-5 border-b border-[rgba(27,42,65,0.1)]" data-testid="storefront-pickup-section">
        <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)] mb-1">Local Pickup Details</div>
        <div className="text-[11px] text-[var(--ink-soft)] mb-3">Shown on the checkout page and in the "ready for pickup" email.</div>
        <div className="grid gap-4 max-w-2xl">
          <label className="block">
            <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--navy)] block mb-1.5">Pickup Address</span>
            <input
              type="text"
              value={pickup.address ?? ""}
              onChange={(e) => setPickup((prev) => ({ ...prev, address: e.target.value }))}
              className="input-field"
              placeholder="312 Whitworth St, Sulphur Springs, TX 75482"
              data-testid="storefront-pickup-address"
            />
          </label>
          <label className="block">
            <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--navy)] block mb-1.5">Pickup Hours</span>
            <input
              type="text"
              value={pickup.hours ?? ""}
              onChange={(e) => setPickup((prev) => ({ ...prev, hours: e.target.value }))}
              className="input-field"
              placeholder="e.g. Mon–Fri 9am–5pm, Sat 10am–2pm"
              data-testid="storefront-pickup-hours"
            />
          </label>
        </div>
        <button type="button" onClick={savePickup} disabled={savingPickup} className="btn-primary mt-4 text-sm" data-testid="storefront-pickup-save">
          <Save className="w-3.5 h-3.5"/> {savingPickup ? "Saving…" : "Save Pickup Details"}
        </button>
      </div>

      <div className="p-5 border-b border-[rgba(27,42,65,0.1)]">
        <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)]">Homepage Photos</div>
        <div className="text-xs text-[var(--ink-soft)] mt-1">Swap the blank product & feature photos shown on the front page. Upload PNG, JPG or WEBP up to 15MB.</div>
      </div>
      <div className="p-5 grid sm:grid-cols-2 gap-5">
        {SLOTS.map((s) => (
          <div key={s.key} className="border border-[rgba(27,42,65,0.1)] rounded-sm p-4" data-testid={`storefront-slot-${s.key}`}>
            <div className="font-serif-display text-lg text-[var(--navy)]">{s.label}</div>
            <div className="font-mono-ui text-[10px] tracking-[0.15em] uppercase text-[var(--ink-soft)] mt-0.5">{s.hint}</div>
            <div className={`mt-3 ${s.ratio} w-full overflow-hidden rounded-sm bg-[var(--bone-2)] border border-[rgba(27,42,65,0.1)]`}>
              {images[s.key] && (
                <img src={imageUrl(images[s.key])} alt={s.label} className="w-full h-full object-cover" data-testid={`storefront-preview-${s.key}`}/>
              )}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <label className="inline-flex items-center gap-2 cursor-pointer btn-ghost py-2 px-3 text-xs">
                <Upload className="w-3.5 h-3.5"/>
                {uploadingKey === s.key ? "Uploading…" : "Upload Photo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  disabled={uploadingKey === s.key}
                  onChange={(e) => { upload(s.key, e.target.files?.[0]); e.target.value = ""; }}
                  data-testid={`storefront-upload-${s.key}`}
                />
              </label>
              {custom[s.key] && (
                <button
                  type="button"
                  onClick={() => reset(s.key)}
                  data-testid={`storefront-reset-${s.key}`}
                  className="inline-flex items-center gap-1.5 text-[var(--crimson)] hover:bg-[var(--crimson)]/10 px-3 py-2 rounded-sm font-mono-ui text-[10px] tracking-[0.2em] uppercase transition"
                >
                  <RotateCcw className="w-3.5 h-3.5"/> Reset
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
